import { useState, useRef, useEffect } from 'react'
import { Icons } from '@/lib/icons'
import { useNotifications } from '@/hooks/useNotifications'
import type { AppNotification } from '@/types/notifications'
import './NotificationBell.css'

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = (notification: AppNotification) => {
    if (!notification.is_read) {
      markRead([notification.id])
    }
    setIsOpen(false)
    // Could add navigation here based on reference_type and reference_id
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <Icons.Alert size={16} className="text-red-500" />
      case 'warning': return <Icons.Alert size={16} className="text-yellow-500" />
      case 'success': return <Icons.Check size={16} className="text-green-500" />
      default: return <Icons.Info size={16} className="text-blue-500" />
    }
  }

  return (
    <div className="sz-notification-bell" ref={dropdownRef}>
      <button 
        className="sz-notification-bell__trigger" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Icons.Bell size={20} />
        {unreadCount > 0 && (
          <span className="sz-notification-bell__badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="sz-notification-bell__dropdown">
          <div className="sz-notification-bell__header">
            <h3 className="sz-notification-bell__title">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="sz-notification-bell__mark-all" 
                onClick={markAllRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          
          <div className="sz-notification-bell__list">
            {notifications.length === 0 ? (
              <div className="sz-notification-bell__empty">
                <Icons.Bell size={24} className="text-gray-300 mb-2" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div 
                  key={n.id} 
                  className={`sz-notification-bell__item ${!n.is_read ? 'sz-notification-bell__item--unread' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="sz-notification-bell__item-icon">
                    {getSeverityIcon(n.severity)}
                  </div>
                  <div className="sz-notification-bell__item-content">
                    <h4 className="sz-notification-bell__item-title">{n.title}</h4>
                    <p className="sz-notification-bell__item-message">{n.message}</p>
                    <span className="sz-notification-bell__item-time">
                      {new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
